import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firestore } from "../firebase";
import {
    Button,
    TextField,
    Select,
    SelectItem,
    Checkbox,
    DateTimeField,
    Paper,
    Chip,
} from "@firecms/ui";

type TargetType = "tier" | "activity" | "manual" | "";

interface CampaignForm {
    name: string;
    description: string;
    target_type: TargetType;
    target_tiers: string[];
    target_last_seen_after: Date | null;
    target_last_seen_before: Date | null;
    target_user_ids: string[];
    text_credits: number;
    voice_credits: number;
    scheduled_for: Date | null;
}

const STEPS = ["Basics", "Targeting", "Rewards", "Review & Create"];

const defaultForm: CampaignForm = {
    name: "",
    description: "",
    target_type: "",
    target_tiers: [],
    target_last_seen_after: null,
    target_last_seen_before: null,
    target_user_ids: [],
    text_credits: 0,
    voice_credits: 0,
    scheduled_for: null,
};

export function CampaignWizard() {
    const [step, setStep] = useState(0);
    const [form, setForm] = useState<CampaignForm>({ ...defaultForm });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [userIdsText, setUserIdsText] = useState("");
    const navigate = useNavigate();

    const updateForm = (patch: Partial<CampaignForm>) => {
        setForm((prev) => ({ ...prev, ...patch }));
        setError(null);
    };

    const canNext = (): boolean => {
        if (step === 0) return form.name.trim().length > 0;
        if (step === 1) {
            if (!form.target_type) return false;
            if (form.target_type === "tier" && form.target_tiers.length === 0) return false;
            if (form.target_type === "activity" && !form.target_last_seen_after && !form.target_last_seen_before) return false;
            if (form.target_type === "manual" && form.target_user_ids.length === 0 && !userIdsText.trim()) return false;
            return true;
        }
        if (step === 2) return form.text_credits > 0 || form.voice_credits > 0;
        return true;
    };

    const handleNext = () => {
        if (step === 1 && form.target_type === "manual" && userIdsText.trim()) {
            const ids = userIdsText
                .split(/[\n,]+/)
                .map((s) => s.trim())
                .filter(Boolean);
            updateForm({ target_user_ids: ids });
        }
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
    };

    const handleBack = () => setStep((s) => Math.max(s - 1, 0));

    const handleSave = async (status: "draft" | "scheduled") => {
        if (status === "scheduled" && !form.scheduled_for) {
            setError("Please select a schedule date/time.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const auth = getAuth();
            const campaignData: Record<string, any> = {
                name: form.name.trim(),
                description: form.description.trim(),
                target_type: form.target_type,
                text_credits: form.text_credits,
                voice_credits: form.voice_credits,
                status,
                created_at: Timestamp.now(),
                created_by: auth.currentUser?.email || "unknown",
                users_targeted: 0,
                users_granted: 0,
            };
            if (form.target_type === "tier") campaignData.target_tiers = form.target_tiers;
            if (form.target_type === "activity") {
                if (form.target_last_seen_after) campaignData.target_last_seen_after = Timestamp.fromDate(form.target_last_seen_after);
                if (form.target_last_seen_before) campaignData.target_last_seen_before = Timestamp.fromDate(form.target_last_seen_before);
            }
            if (form.target_type === "manual") campaignData.target_user_ids = form.target_user_ids;
            if (status === "scheduled" && form.scheduled_for) {
                campaignData.scheduled_for = Timestamp.fromDate(form.scheduled_for);
            }

            await addDoc(collection(firestore, "campaigns"), campaignData);
            navigate("/c/campaigns");
        } catch (e: any) {
            setError(e.message || "Failed to create campaign");
        } finally {
            setSaving(false);
        }
    };

    const toggleTier = (tier: string) => {
        updateForm({
            target_tiers: form.target_tiers.includes(tier)
                ? form.target_tiers.filter((t) => t !== tier)
                : [...form.target_tiers, tier],
        });
    };

    const targetTypeLabel = (t: string) =>
        ({ tier: "By Subscription Tier", activity: "By Activity (Last Seen)", manual: "Manual User IDs" }[t] || t);

    // ── Step UI ──────────────────────────────────────────────

    const renderBasics = () => (
        <div className="space-y-6">
            <div>
                <label className="block text-sm font-bold text-slate-400 mb-2 uppercase tracking-wider">Campaign Name *</label>
                <TextField
                    value={form.name}
                    onChange={(e) => updateForm({ name: e.target.value })}
                    size="medium"
                    className="w-full"
                />
            </div>
            <div>
                <label className="block text-sm font-bold text-slate-400 mb-2 uppercase tracking-wider">Description</label>
                <textarea
                    value={form.description}
                    onChange={(e) => updateForm({ description: e.target.value })}
                    rows={4}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none transition-colors resize-none"
                    placeholder="Describe the purpose of this campaign..."
                />
            </div>
        </div>
    );

    const renderTargeting = () => (
        <div className="space-y-6">
            <div>
                <label className="block text-sm font-bold text-slate-400 mb-3 uppercase tracking-wider">Target Type *</label>
                <Select
                    value={form.target_type}
                    onValueChange={(v) => updateForm({ target_type: v as TargetType, target_tiers: [], target_user_ids: [], target_last_seen_after: null, target_last_seen_before: null })}
                    renderValue={(v) => targetTypeLabel(v as string)}
                    className="w-full"
                >
                    <SelectItem value="tier">By Subscription Tier</SelectItem>
                    <SelectItem value="activity">By Activity (Last Seen)</SelectItem>
                    <SelectItem value="manual">Manual User IDs</SelectItem>
                </Select>
            </div>

            {form.target_type === "tier" && (
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 space-y-3">
                    <p className="text-sm text-slate-400 font-medium">Select tiers to target:</p>
                    {["free", "pro"].map((tier) => (
                        <label key={tier} className="flex items-center gap-3 cursor-pointer group">
                            <Checkbox
                                checked={form.target_tiers.includes(tier)}
                                onCheckedChange={() => toggleTier(tier)}
                            />
                            <span className="text-white font-medium capitalize group-hover:text-indigo-400 transition-colors">{tier}</span>
                        </label>
                    ))}
                </div>
            )}

            {form.target_type === "activity" && (
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 space-y-4">
                    <p className="text-sm text-slate-400 font-medium">Filter by last seen date range:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs text-slate-500 mb-1">Active After</label>
                            <DateTimeField
                                value={form.target_last_seen_after}
                                onChange={(d) => updateForm({ target_last_seen_after: d })}
                                mode="date"
                                clearable
                                size="small"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-slate-500 mb-1">Active Before</label>
                            <DateTimeField
                                value={form.target_last_seen_before}
                                onChange={(d) => updateForm({ target_last_seen_before: d })}
                                mode="date"
                                clearable
                                size="small"
                            />
                        </div>
                    </div>
                </div>
            )}

            {form.target_type === "manual" && (
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 space-y-3">
                    <p className="text-sm text-slate-400 font-medium">Enter Telegram user IDs (one per line or comma-separated):</p>
                    <textarea
                        value={userIdsText}
                        onChange={(e) => setUserIdsText(e.target.value)}
                        rows={5}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white font-mono text-sm placeholder-slate-500 focus:border-indigo-500 focus:outline-none transition-colors resize-none"
                        placeholder={"123456789\n987654321\n..."}
                    />
                    {userIdsText.trim() && (
                        <p className="text-xs text-slate-500">
                            {userIdsText.split(/[\n,]+/).filter((s) => s.trim()).length} user(s) entered
                        </p>
                    )}
                </div>
            )}
        </div>
    );

    const renderRewards = () => (
        <div className="space-y-6">
            <p className="text-slate-400 text-sm">Define the credits to grant to each targeted user.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 space-y-3">
                    <label className="block text-sm font-bold text-emerald-400 uppercase tracking-wider">💬 Text Credits</label>
                    <input
                        type="number"
                        min={0}
                        value={form.text_credits}
                        onChange={(e) => updateForm({ text_credits: Math.max(0, parseInt(e.target.value) || 0) })}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white text-2xl font-bold text-center focus:border-emerald-500 focus:outline-none transition-colors"
                    />
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 space-y-3">
                    <label className="block text-sm font-bold text-blue-400 uppercase tracking-wider">🎙️ Voice Credits</label>
                    <input
                        type="number"
                        min={0}
                        value={form.voice_credits}
                        onChange={(e) => updateForm({ voice_credits: Math.max(0, parseInt(e.target.value) || 0) })}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white text-2xl font-bold text-center focus:border-blue-500 focus:outline-none transition-colors"
                    />
                </div>
            </div>

            {/* Preview card */}
            {(form.text_credits > 0 || form.voice_credits > 0) && (
                <div className="bg-gradient-to-br from-indigo-900/40 to-purple-900/40 border border-indigo-500/30 rounded-2xl p-6 text-center">
                    <p className="text-xs text-indigo-300 uppercase tracking-widest font-bold mb-2">Reward Preview</p>
                    <div className="flex items-center justify-center gap-6">
                        {form.text_credits > 0 && (
                            <div>
                                <span className="text-3xl font-black text-emerald-400">+{form.text_credits}</span>
                                <p className="text-xs text-slate-400 mt-1">text credits</p>
                            </div>
                        )}
                        {form.text_credits > 0 && form.voice_credits > 0 && (
                            <span className="text-slate-600 text-2xl">+</span>
                        )}
                        {form.voice_credits > 0 && (
                            <div>
                                <span className="text-3xl font-black text-blue-400">+{form.voice_credits}</span>
                                <p className="text-xs text-slate-400 mt-1">voice credits</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    const renderReview = () => (
        <div className="space-y-6">
            {/* Summary card */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl divide-y divide-slate-700">
                <div className="p-4 flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Name</span>
                    <span className="text-white font-bold">{form.name}</span>
                </div>
                {form.description && (
                    <div className="p-4 flex justify-between items-start">
                        <span className="text-slate-400 text-sm">Description</span>
                        <span className="text-white text-sm max-w-xs text-right">{form.description}</span>
                    </div>
                )}
                <div className="p-4 flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Target</span>
                    <div className="text-right">
                        <span className="text-white font-mono text-sm">{targetTypeLabel(form.target_type)}</span>
                        {form.target_type === "tier" && (
                            <div className="flex gap-1 mt-1 justify-end">
                                {form.target_tiers.map((t) => (
                                    <Chip key={t} size="small">{t}</Chip>
                                ))}
                            </div>
                        )}
                        {form.target_type === "manual" && (
                            <p className="text-xs text-slate-500 mt-1">{form.target_user_ids.length} user(s)</p>
                        )}
                        {form.target_type === "activity" && (
                            <p className="text-xs text-slate-500 mt-1">
                                {form.target_last_seen_after && `After: ${form.target_last_seen_after.toLocaleDateString()}`}
                                {form.target_last_seen_after && form.target_last_seen_before && " · "}
                                {form.target_last_seen_before && `Before: ${form.target_last_seen_before.toLocaleDateString()}`}
                            </p>
                        )}
                    </div>
                </div>
                <div className="p-4 flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Rewards</span>
                    <div className="flex gap-4">
                        <span className="text-emerald-400 font-bold">+{form.text_credits} text</span>
                        <span className="text-blue-400 font-bold">+{form.voice_credits} voice</span>
                    </div>
                </div>
            </div>

            {/* Schedule option */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 space-y-3">
                <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider">Schedule (Optional)</label>
                <p className="text-xs text-slate-500">Set a date to execute later, or leave empty to save as draft.</p>
                <DateTimeField
                    value={form.scheduled_for}
                    onChange={(d) => updateForm({ scheduled_for: d })}
                    mode="date_time"
                    clearable
                    size="small"
                />
            </div>

            {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 text-sm">
                    {error}
                </div>
            )}
        </div>
    );

    const stepContent = [renderBasics, renderTargeting, renderRewards, renderReview];

    return (
        <div className="p-8 max-w-3xl mx-auto min-h-screen">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-black text-white tracking-tight">Create Campaign</h1>
                <p className="text-slate-500 mt-1">Step-by-step campaign creation wizard.</p>
            </div>

            {/* Stepper */}
            <div className="flex items-center gap-1 mb-10">
                {STEPS.map((label, i) => (
                    <div key={i} className="flex items-center gap-1 flex-1">
                        <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold shrink-0 transition-all ${i < step ? "bg-indigo-600 text-white" :
                                i === step ? "bg-indigo-500 text-white ring-4 ring-indigo-500/20" :
                                    "bg-slate-800 text-slate-500"
                            }`}>
                            {i < step ? "✓" : i + 1}
                        </div>
                        <span className={`text-xs font-medium hidden sm:inline ${i <= step ? "text-white" : "text-slate-600"
                            }`}>
                            {label}
                        </span>
                        {i < STEPS.length - 1 && (
                            <div className={`flex-1 h-0.5 mx-2 rounded ${i < step ? "bg-indigo-600" : "bg-slate-800"
                                }`} />
                        )}
                    </div>
                ))}
            </div>

            {/* Step content */}
            <Paper className="bg-slate-900 border border-slate-800 rounded-2xl p-8 mb-8">
                <h2 className="text-xl font-bold text-white mb-6">{STEPS[step]}</h2>
                {stepContent[step]()}
            </Paper>

            {/* Navigation */}
            <div className="flex justify-between items-center">
                <div>
                    {step > 0 && (
                        <Button onClick={handleBack} variant="text" className="text-slate-400">
                            ← Back
                        </Button>
                    )}
                </div>
                <div className="flex gap-3">
                    {step < STEPS.length - 1 ? (
                        <Button
                            onClick={handleNext}
                            disabled={!canNext()}
                            variant="filled"
                            color="primary"
                        >
                            Next →
                        </Button>
                    ) : (
                        <>
                            <Button
                                onClick={() => handleSave("draft")}
                                disabled={saving}
                                variant="outlined"
                            >
                                {saving ? "Saving..." : "📝 Save as Draft"}
                            </Button>
                            {form.scheduled_for && (
                                <Button
                                    onClick={() => handleSave("scheduled")}
                                    disabled={saving}
                                    variant="filled"
                                    color="primary"
                                >
                                    {saving ? "Saving..." : "📅 Schedule"}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
